import styled from '@emotion/styled/macro';
import {
  ARCSubStatus,
  Button,
  Card,
  CardHeader,
  CheckoutSubStatus,
  CityInspectionSubStatus,
  CityPermittingSubStatus,
  DesignApprovalSubStatus,
  HomeReviewSubStatus,
  HqsStatus,
  InstallationSubStatus,
  InstallPrepSubStatus,
  NewCustomerSubStatus,
  OfferCreatedSubStatus,
  OpenTasks,
  Pill,
  PipelineUrgentActionBanner,
  PowerOnSubStatus,
  SfdcEnv,
  SlidingDrawer,
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
  useSingletonMobileMediaQuery,
  NotOptimizedForMobileModal,
  useSingletonTabletMediaQuery,
  MOBILE_BREAKPOINT,
} from '@sunrun/experience-ui-components';
import {
  FIELD_SALES,
  FUSION_CLOSER,
  FUSION_SETTER,
  getEnvironment,
  getSfdcEnv,
  INSIDE_SALES,
  CX_PRO,
} from '@sunrun/sales-experience-shared';
import {
  useFeatureFlagEnabled,
  useFeatureFlagString,
} from '@sunrun/sales-experience-shared/react-feature-flags';
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ChatIframe } from '../atoms/ChatIframe';
import { AppointmentView } from 'components/atoms/AppointmentView';
// eslint-disable-next-line import/no-restricted-paths
import { SharePresentationModal } from 'components/organisms/SharePresentationModal';
// eslint-disable-next-line import/no-restricted-paths
import { ShareBillModal } from 'components/organisms/ShareBillModal';
import { formatCurrency, formatDate, formatTime } from 'helpers/formatHelpers';
import {
  getProspectCommissionAmount,
  stageShowsCommissionTotal,
} from 'helpers/pipelineCommissionHelpers';
import { isAffiliateHelper } from 'helpers/repTypeHelper';
import { OpenCustomerProfileOptions } from 'helpers/listViewHelpers';
import { useHybridToken } from 'providers/Auth';
import { getHQS } from 'providers/HQS';
import {
  deletePipelineCache,
  PipelineProspect,
  updateFavorite,
} from 'providers/Pipeline';
import { useUserProfile } from 'providers/UserProfile';
import {
  CHECKOUT,
  CITY_INSPECTION,
  CITY_PERMITTING,
  DESIGN_APPROVAL,
  HOME_REVIEW,
  INSTALL_PREP,
  INSTALLATION,
  NEW_CUSTOMER,
  OFFER_CREATED,
  POWER_ON,
  ARC,
  UNKNOWN,
  PIPELINE_DRAWER_HOST_ID,
} from 'components/atoms/PipelineConsts';
import { FEATURE_FLAGS } from 'constants/FeatureFlags';

const SPECIALIST_COMMISSION_CUTOFF = new Date('2026-04-21T00:00:00.000Z');

type PipelineCardProps = {
  pipelineProspect: PipelineProspect;
  includeSalesRep: boolean;
  showCommission: boolean;
  allowJobCode: boolean;
  numUnreadChats: number;
  showInstallScheduleModalAndGetUrl: (prospect: PipelineProspect) => void;
  openCustomerProfile: (
    prospectId: string,
    options?: OpenCustomerProfileOptions,
  ) => void;
  splatBaseUrl: string;
  oneBaseUrl: string;
  salesforceBaseUrl: string;
  sfPartnerBaseUrl: string;
  sfdcEnv: SfdcEnv;
};

const PipelineCard = memo(
  ({
    pipelineProspect,
    includeSalesRep,
    showCommission,
    allowJobCode,
    numUnreadChats: initialNumUnreadChats,
    showInstallScheduleModalAndGetUrl,
    openCustomerProfile,
    splatBaseUrl,
    oneBaseUrl,
    salesforceBaseUrl,
    sfPartnerBaseUrl,
    sfdcEnv,
  }: PipelineCardProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [drawerHost, setDrawerHost] = useState<HTMLElement | null>(null);
    const [isFavoriteSaving, setIsFavoriteSaving] = useState(false);
    const [numUnreadChats, setNumUnreadChats] = useState(initialNumUnreadChats);
    useEffect(() => {
      setNumUnreadChats(initialNumUnreadChats);
    }, [initialNumUnreadChats]);
    useLayoutEffect(() => {
      setDrawerHost(document.getElementById(PIPELINE_DRAWER_HOST_ID));
    }, []);
    const [hqsStatus, setHqsStatus] = useState<HqsStatus>(
      pipelineProspect.pipelineStage === NEW_CUSTOMER ||
        pipelineProspect.pipelineStage === OFFER_CREATED
        ? 'LOADING'
        : 'INELIGIBLE',
    );
    const [isNotMobileOptimizedModalOpen, setIsNotMobileOptimizedModalOpen] =
      useState(false);
    const [isSharePresentationOpen, setIsSharePresentationOpen] =
      useState(false);
    const [isShareBillOpen, setIsShareBillOpen] = useState(false);
    const env = getEnvironment();

    const { userProfile, splatUserType } = useUserProfile();
    const { hybridToken, hybridTokenType } = useHybridToken();

    const isFusionExpert =
      userProfile?.sellsSunrun === 'Yes' && userProfile?.isFusion;
    const isMobile = useSingletonMobileMediaQuery();
    const isTablet = useSingletonTabletMediaQuery();

    const configuredPartnerSfdcUrl = useFeatureFlagString(
      FEATURE_FLAGS.SFDC_PARTNER_URL,
    );
    const configuredSfdcUrl = useFeatureFlagString(FEATURE_FLAGS.SFDC_URL);
    const isSalesChatEnabled = useFeatureFlagEnabled(
      FEATURE_FLAGS.SALES_CHAT_ENABLED,
    );
    const isNewPresentationEnabled = useFeatureFlagEnabled(
      FEATURE_FLAGS.NEW_PRESENTATION,
    );

    const {
      salesforceUrl,
      canUseAppointments,
      useJobCode,
      isOwner,
      useFusionCommission,
      projectUrl,
    } = useMemo(() => {
      if (!userProfile) return {};

      const isOwner = pipelineProspect.salesRepId === userProfile.contactId;

      let salesforceUrl;
      let projectUrl = undefined;
      switch (getSfdcEnv(userProfile)) {
        case 'Community': {
          const baseUrl =
            configuredPartnerSfdcUrl ??
            (env === 'production'
              ? 'https://sunrun.my.site.com'
              : 'https://sunrun--uat.sandbox.my.site.com');
          const sfdcBaseUrl = `${baseUrl}/partners`;
          salesforceUrl = `${sfdcBaseUrl}/s/partnerOpp?id=${pipelineProspect.opportunityId}`;
          break;
        }
        case 'D2HCommunity': {
          const baseUrl =
            configuredPartnerSfdcUrl ??
            (env === 'production'
              ? 'https://sunrun.my.site.com'
              : 'https://sunrun--uat.sandbox.my.site.com');
          const sfdcBaseUrl = `${baseUrl}/d2hpartnercommunity`;
          salesforceUrl = `${sfdcBaseUrl}/PartnerOpportunityCreate?id=${pipelineProspect.opportunityId}`;
          if (pipelineProspect.projectId) {
            projectUrl = `${sfdcBaseUrl}/WFProjectRedirectPage?id=${
              pipelineProspect.projectId
            }${isOwner ? '' : '&mgrView=1'}`;
          }
          break;
        }
        case 'SFDC':
        case 'None':
        default: {
          const sfdcBaseUrl =
            configuredSfdcUrl ??
            (env === 'production'
              ? 'https://sunrun.my.salesforce.com'
              : 'https://sunrun--uat.sandbox.my.salesforce.com');
          salesforceUrl = `${sfdcBaseUrl}/${pipelineProspect.opportunityId}`;
          if (pipelineProspect.projectId) {
            projectUrl = `${sfdcBaseUrl}/${pipelineProspect.projectId}`;
          }
          break;
        }
      }

      const canUseAppointments =
        userProfile?.splatRole === INSIDE_SALES ||
        userProfile?.splatRole === FIELD_SALES ||
        userProfile?.splatRole === FUSION_CLOSER ||
        userProfile?.splatRole === FUSION_SETTER;

      const useJobCode = pipelineProspect.jobCode && allowJobCode;

      const useFusionCommission = userProfile.isFusion;
      return {
        salesforceUrl,
        canUseAppointments,
        useJobCode,
        isOwner,
        useFusionCommission,
        projectUrl,
      };
    }, [
      allowJobCode,
      configuredPartnerSfdcUrl,
      configuredSfdcUrl,
      pipelineProspect,
      userProfile,
      env,
    ]);

    const daysAgoText = useMemo(() => {
      const daysAgo = Math.floor(
        (new Date().getTime() -
          Date.parse(pipelineProspect.statusDate + '-0000')) /
          1000 /
          60 /
          60 /
          24,
      );
      if (daysAgo <= 1) {
        return 'Updated today';
      }
      if (daysAgo === 1) {
        return 'Updated yesterday';
      }
      return `Updated ${daysAgo} days ago`;
    }, [pipelineProspect.statusDate]);

    const starClick = useCallback(() => {
      setIsFavoriteSaving(true);
      updateFavorite(
        hybridToken,
        pipelineProspect,
        !pipelineProspect.starredInPipeline,
      )
        .then(() =>
          pipelineProspect.salesRepId
            ? deletePipelineCache(hybridToken, pipelineProspect.salesRepId)
            : undefined,
        )
        .then(() => {
          pipelineProspect.starredInPipeline =
            !pipelineProspect.starredInPipeline;
        })
        .catch(() => {
          // Do nothing
        })
        .finally(() => setIsFavoriteSaving(false));
    }, [hybridToken, pipelineProspect]);

    const handleChatClick = () => {
      setIsChatOpen(true);
      setNumUnreadChats(0);
    };

    const mobileOpenOfferPresentation = useCallback(() => {
      setIsNotMobileOptimizedModalOpen(true);
    }, []);

    const openOfferPresentation = useCallback(() => {
      const { username, email, splatRole } = userProfile ?? {};
      const userName = encodeURIComponent(username ?? '');
      const userEmail = encodeURIComponent(email ?? '');
      const userRole = encodeURIComponent(splatRole ?? '');

      const redirectUrl = encodeURIComponent(
        `/go-solar/${pipelineProspect.prospectId}?prospectId=${pipelineProspect.prospectId}&username=${userName}&email=${userEmail}&role=${userRole}`,
      );
      const url = `${splatBaseUrl}/login?token=${hybridToken}&tokenType=hybrid-${hybridTokenType}&redirect=${redirectUrl}`;
      window.open(url, '_blank');
      setIsNotMobileOptimizedModalOpen(false);
    }, [
      hybridToken,
      hybridTokenType,
      pipelineProspect,
      splatBaseUrl,
      userProfile,
    ]);

    const openNewOfferPresentation = useCallback(() => {
      const redirectUrl = encodeURIComponent(
        `/slideshow/${pipelineProspect.prospectId}`,
      );
      const url = `https://staging.presentation.sunrunone.com/transfer?token=${hybridToken}&tokenType=hybrid-${hybridTokenType}&redirect=${redirectUrl}`;
      window.open(url, '_blank');
      setIsNotMobileOptimizedModalOpen(false);
    }, [hybridToken, hybridTokenType, pipelineProspect]);

    const navigateToCustomerProfile = useCallback(() => {
      window.open(
        `${oneBaseUrl}/customers/customer-profile/${pipelineProspect.prospectId}`,
        '_blank',
      );
    }, [oneBaseUrl, pipelineProspect.prospectId]);

    const emailCustomer = useCallback(() => {
      window.open(`mailto:${pipelineProspect.email}`, '_blank');
    }, [pipelineProspect]);

    const viewInSalesforce = useCallback(() => {
      window.open(salesforceUrl, '_blank');
    }, [salesforceUrl]);

    const viewProject = useCallback(() => {
      window.open(projectUrl, '_blank');
    }, [projectUrl]);

    const copyCustomerInformation = useCallback(() => {
      navigator.clipboard.writeText(
        `Customer: ${pipelineProspect.firstName} ${
          pipelineProspect.lastName
        }\nAddress: ${pipelineProspect.address}\n${
          useJobCode
            ? 'Job code: ' + pipelineProspect.jobCode
            : 'Prospect: ' + pipelineProspect.prospectId
        }`,
      );
      setIsOpen(false);
    }, [pipelineProspect, useJobCode]);

    const pipelineStatus = useMemo(() => {
      switch (pipelineProspect.pipelineStage) {
        case NEW_CUSTOMER:
          return (
            <NewCustomerSubStatus
              canUseAppointments={canUseAppointments || false}
              hqsStatus={hqsStatus}
              pipelineProspect={pipelineProspect}
              splatBaseUrl={splatBaseUrl}
              oneBaseUrl={oneBaseUrl}
              salesforceBaseUrl={salesforceBaseUrl}
              sfPartnerBaseUrl={sfPartnerBaseUrl}
              sfdcEnv={sfdcEnv}
              isDisabled={!isOwner}
              token={hybridToken}
              tokenType={hybridTokenType}></NewCustomerSubStatus>
          );
        case OFFER_CREATED:
          return (
            <OfferCreatedSubStatus
              canUseAppointments={canUseAppointments || false}
              hqsStatus={hqsStatus}
              pipelineProspect={pipelineProspect}
              splatBaseUrl={splatBaseUrl}
              oneBaseUrl={oneBaseUrl}
              isDisabled={!isOwner}
              token={hybridToken}
              tokenType={hybridTokenType}></OfferCreatedSubStatus>
          );
        case CHECKOUT:
          return (
            <CheckoutSubStatus
              pipelineProspect={pipelineProspect}
              splatBaseUrl={splatBaseUrl}
              salesforceBaseUrl={salesforceBaseUrl}
              sfPartnerBaseUrl={sfPartnerBaseUrl}
              sfdcEnv={sfdcEnv}
              isDisabled={!isOwner}
              token={hybridToken}
              tokenType={hybridTokenType}></CheckoutSubStatus>
          );
        case HOME_REVIEW:
          return (
            <HomeReviewSubStatus
              pipelineProspect={pipelineProspect}
              splatBaseUrl={splatBaseUrl}
              salesforceBaseUrl={salesforceBaseUrl}
              sfPartnerBaseUrl={sfPartnerBaseUrl}
              sfdcEnv={sfdcEnv}
              isDisabled={!isOwner}
              token={hybridToken}
              tokenType={hybridTokenType}></HomeReviewSubStatus>
          );
        case DESIGN_APPROVAL:
          return (
            <DesignApprovalSubStatus
              showNextStep={false}
              pipelineProspect={pipelineProspect}
              splatBaseUrl={splatBaseUrl}
              salesforceBaseUrl={salesforceBaseUrl}
              sfPartnerBaseUrl={sfPartnerBaseUrl}
              sfdcEnv={sfdcEnv}
              isDisabled={!isOwner}
              token={hybridToken}
              setIsNotMobileOptimizedModalOpen={mobileOpenOfferPresentation}
              tokenType={hybridTokenType}></DesignApprovalSubStatus>
          );
        case CITY_PERMITTING:
          return (
            <CityPermittingSubStatus
              pipelineProspect={pipelineProspect}
              salesforceBaseUrl={salesforceBaseUrl}
              sfPartnerBaseUrl={sfPartnerBaseUrl}
              sfdcEnv={sfdcEnv}
              isDisabled={!isOwner}></CityPermittingSubStatus>
          );
        case INSTALL_PREP:
          return (
            <InstallPrepSubStatus
              pipelineProspect={pipelineProspect}
              salesforceBaseUrl={salesforceBaseUrl}
              sfPartnerBaseUrl={sfPartnerBaseUrl}
              sfdcEnv={sfdcEnv}
              isDisabled={!isOwner}></InstallPrepSubStatus>
          );
        case INSTALLATION:
          return (
            <InstallationSubStatus
              isPartner={userProfile?.isPartner}
              pipelineProspect={pipelineProspect}
              salesforceBaseUrl={salesforceBaseUrl}
              sfPartnerBaseUrl={sfPartnerBaseUrl}
              sfdcEnv={sfdcEnv}
              isDisabled={!isOwner}></InstallationSubStatus>
          );
        case CITY_INSPECTION:
          return (
            <CityInspectionSubStatus
              isPartner={userProfile?.isPartner}
              pipelineProspect={pipelineProspect}
              salesforceBaseUrl={salesforceBaseUrl}
              sfPartnerBaseUrl={sfPartnerBaseUrl}
              sfdcEnv={sfdcEnv}
              isDisabled={!isOwner}></CityInspectionSubStatus>
          );
        case POWER_ON:
          return (
            <PowerOnSubStatus
              isPartner={userProfile?.isPartner}
              pipelineProspect={pipelineProspect}
              salesforceBaseUrl={salesforceBaseUrl}
              sfPartnerBaseUrl={sfPartnerBaseUrl}
              sfdcEnv={sfdcEnv}
              isDisabled={!isOwner}
            />
          );
        case ARC:
          return (
            <ARCSubStatus
              pipelineProspect={pipelineProspect}
              salesforceBaseUrl={salesforceBaseUrl}
              sfPartnerBaseUrl={sfPartnerBaseUrl}
              sfdcEnv={sfdcEnv}
              isDisabled={!isOwner}></ARCSubStatus>
          );
        case UNKNOWN:
        default:
          return <div></div>;
      }
    }, [
      canUseAppointments,
      hqsStatus,
      hybridToken,
      hybridTokenType,
      isOwner,
      oneBaseUrl,
      pipelineProspect,
      salesforceBaseUrl,
      sfPartnerBaseUrl,
      sfdcEnv,
      splatBaseUrl,
      userProfile?.isPartner,
      mobileOpenOfferPresentation,
    ]);

    const openTasks = useMemo(() => {
      // Hide open tasks for affiliate partners
      if (userProfile?.isPartner) {
        return null;
      }

      const prospectOpenTasks = pipelineProspect.openTasks ?? [];
      if (prospectOpenTasks.length > 0) {
        return (
          <OpenTasks
            pipelineProspect={pipelineProspect}
            salesforceBaseUrl={salesforceBaseUrl}
            sfPartnerBaseUrl={sfPartnerBaseUrl}
            sfdcEnv={sfdcEnv}
            isDisabled={!isOwner}
          />
        );
      }

      return null;
    }, [
      isOwner,
      pipelineProspect,
      salesforceBaseUrl,
      sfPartnerBaseUrl,
      sfdcEnv,
      userProfile?.isPartner,
    ]);

    const isOnOrAfterSpecialistCommissionCutoff =
      pipelineProspect.opportunityCreatedDate != null &&
      new Date(pipelineProspect.opportunityCreatedDate) >=
        SPECIALIST_COMMISSION_CUTOFF;

    const showSpecialistCommissions =
      isFusionExpert &&
      pipelineProspect.fusionCommissionSpecialist !== null &&
      isOnOrAfterSpecialistCommissionCutoff;

    const showCommissionPills =
      showCommission &&
      stageShowsCommissionTotal(pipelineProspect.pipelineStage);

    const commissionAmount = getProspectCommissionAmount(
      pipelineProspect,
      useFusionCommission ?? false,
    );

    const specialistCommissionAmount = useFusionCommission
      ? pipelineProspect.fusionCommissionSpecialist
      : pipelineProspect.fusionCommission;

    const canSendShareBill = useMemo(() => {
      if (!isOwner) {
        return false;
      }

      return (
        pipelineProspect.pipelineStage === NEW_CUSTOMER &&
        !pipelineProspect.hasUtilityBill
      );
    }, [isOwner, pipelineProspect]);

    const canSendOfferPresentation = useMemo(() => {
      if (!isOwner) {
        return false;
      }

      switch (pipelineProspect.pipelineStage) {
        case OFFER_CREATED:
        case CHECKOUT:
          return true;
        default:
          return false;
      }
    }, [isOwner, pipelineProspect]);

    const isAffiliate = userProfile ? isAffiliateHelper(userProfile) : false;

    useEffect(() => {
      if (isOpen && hqsStatus === 'LOADING') {
        getHQS(hybridToken, pipelineProspect.prospectId)
          .then((result) => setHqsStatus(result))
          .catch(() => {
            // Do nothing.
          });
      }
    }, [hqsStatus, hybridToken, isOpen, pipelineProspect.prospectId]);

    const siteSurveyAppointment = useMemo(() => {
      const siteSurveyAppointments =
        pipelineProspect.serviceAppointments
          ?.filter((appointment) => appointment.recordType === 'Site Survey')
          .sort((a, b) => {
            return (
              new Date(a.arrivalWindowStartTime).getTime() -
              new Date(b.arrivalWindowStartTime).getTime()
            );
          }) ?? [];
      return siteSurveyAppointments.length > 0
        ? siteSurveyAppointments[0]
        : null;
    }, [pipelineProspect]);

    const isPreSale = [NEW_CUSTOMER, OFFER_CREATED, CHECKOUT].includes(
      pipelineProspect.pipelineStage,
    );

    return (
      <>
        {isNotMobileOptimizedModalOpen && (
          <NotOptimizedForMobileModal
            onContinueAnyway={openOfferPresentation}
            onClose={() => setIsNotMobileOptimizedModalOpen(false)}
          />
        )}
        {isShareBillOpen && (
          <ShareBillModal
            hybridToken={hybridToken}
            prospectId={pipelineProspect.prospectId}
            onDismiss={() => setIsShareBillOpen(false)}
            name={`${pipelineProspect.firstName} ${pipelineProspect.lastName}`}
            email={pipelineProspect.email}
            isAffiliate={isAffiliate}
          />
        )}
        {isSharePresentationOpen && (
          <SharePresentationModal
            hybridToken={hybridToken}
            prospectId={pipelineProspect.prospectId}
            onDismiss={() => setIsSharePresentationOpen(false)}
            name={`${pipelineProspect.firstName} ${pipelineProspect.lastName}`}
            email={pipelineProspect.email}
            isAffiliate={isAffiliate}
          />
        )}
        <PipelineStyledCard key={pipelineProspect.key}>
          <PipelineCardHeader>
            <TitleContainer>
              {pipelineProspect.firstName} {pipelineProspect.lastName}
            </TitleContainer>
            <IconContainer>
              {(isOwner || splatUserType === 'CX Pro') &&
                pipelineProspect.contactId &&
                (isSalesChatEnabled || splatUserType === 'CX Pro') && (
                  <MenuButton
                    data-testid="chat-button"
                    color="tertiary"
                    onClick={handleChatClick}
                    iconMiddle={
                      numUnreadChats > 0 ? (
                        <ChatIconWithBadge>
                          <StyledSvgIcon
                            data-testid="unread-chat-icon"
                            name={SvgNames.ChatTearDropFilled}
                            width={24}
                            height={24}
                            color="black"
                          />
                          <UnreadCount>
                            {numUnreadChats > 9 ? '9+' : numUnreadChats}
                          </UnreadCount>
                        </ChatIconWithBadge>
                      ) : (
                        <StyledSvgIcon
                          data-testid="no-unread-chat-icon"
                          name={SvgNames.ChatTearDrop}
                          width={24}
                          height={24}
                          color="black"
                        />
                      )
                    }
                  />
                )}
              {splatUserType !== CX_PRO && (
                <MenuButton
                  data-testid="star-button"
                  color="tertiary"
                  onClick={starClick}
                  disabled={isFavoriteSaving}
                  iconMiddle={
                    <SvgIcon
                      name={
                        isFavoriteSaving
                          ? SvgNames.Loading
                          : pipelineProspect.starredInPipeline
                          ? SvgNames.StarFilled
                          : SvgNames.Star
                      }
                      width={24}
                      height={24}
                      color="black"
                    />
                  }
                />
              )}
            </IconContainer>
          </PipelineCardHeader>
          {userProfile && (
            <StyledPipelineUrgentActionBanner
              pipelineProspect={pipelineProspect}
              oneBaseUrl={oneBaseUrl}
              salesforceBaseUrl={salesforceBaseUrl}
              sfdcEnv={sfdcEnv}
              sfPartnerBaseUrl={sfPartnerBaseUrl}
              splatBaseUrl={splatBaseUrl}
              token={hybridToken}
              tokenType={hybridTokenType}
              showInstallScheduleModalAndGetUrl={() =>
                showInstallScheduleModalAndGetUrl(pipelineProspect)
              }
              viewCapNextSteps={
                openCustomerProfile
                  ? () => openCustomerProfile(pipelineProspect.prospectId)
                  : undefined
              }
              viewPendingTask={
                openCustomerProfile
                  ? () =>
                      openCustomerProfile(pipelineProspect.prospectId, {
                        openPendingTask: true,
                      })
                  : undefined
              }
            />
          )}
          <PipelineCardBodyContainer>
            <PipelineCardBody>
              <div>{pipelineProspect.address}</div>
              {showCommissionPills &&
                (showSpecialistCommissions ? (
                  <CommissionPillContainer>
                    <CommissionPill
                      text={`Est. expert commission: ${formatCurrency(
                        commissionAmount,
                        0,
                      )}`}
                      type="success"
                    />
                    <CommissionPill
                      text={`Est. specialist commission: ${formatCurrency(
                        specialistCommissionAmount,
                        0,
                      )}`}
                      type="info"
                    />
                  </CommissionPillContainer>
                ) : (
                  <CommissionPill
                    text={`Est. net commission: ${formatCurrency(
                      commissionAmount,
                      0,
                    )}`}
                    type="success"
                  />
                ))}
            </PipelineCardBody>
            {includeSalesRep && (
              <SalesRepInfo>
                <Typography>Sales rep</Typography>
                <Typography>{pipelineProspect.salesRepName}</Typography>
              </SalesRepInfo>
            )}
            <PipelineStatus className={isOpen ? '' : 'single'}>
              {pipelineStatus}
              {openTasks}
            </PipelineStatus>
            <ProspectInfo className={isOpen ? 'visible' : ''}>
              {canUseAppointments &&
                isPreSale &&
                pipelineProspect.highlightAppointment && (
                  <AppointmentView
                    appointment={pipelineProspect.highlightAppointment}
                    timezone={pipelineProspect.timezone}
                  />
                )}
              {siteSurveyAppointment && (
                <UpcomingAppointment>
                  <HomeReviewLayout>
                    <div>
                      <Flex>
                        <SvgIcon
                          style={{ display: 'flex', marginRight: 4 }}
                          width={16}
                          height={16}
                          color={tokens.TINTS_OFF_WHITE_20}
                          name={SvgNames.Appointments}
                        />{' '}
                        <Typography size={12}>Home Review</Typography>
                      </Flex>

                      <Typography
                        size={12}
                        style={{ display: 'block', marginLeft: 20 }}>
                        {formatTime(
                          siteSurveyAppointment.arrivalWindowStartTime,
                        )}{' '}
                        -{' '}
                        {formatDate(siteSurveyAppointment.arrivalWindowEndTime)}
                      </Typography>
                    </div>
                    <SurveyPill
                      text={siteSurveyAppointment.status}
                      type="info"
                      hideIcon
                    />
                  </HomeReviewLayout>
                </UpcomingAppointment>
              )}
              <ProspectInfoItem>
                <Typography>Phone Number</Typography>
                <a href={`tel:${pipelineProspect.phone}`}>
                  {pipelineProspect.phone}
                </a>
              </ProspectInfoItem>
              <ProspectInfoItem>
                <Typography>Email</Typography>
                <Button color="link" onClick={emailCustomer}>
                  {pipelineProspect.email}
                </Button>
              </ProspectInfoItem>
              {!useJobCode && (
                <ProspectInfoItem>
                  <Typography>Prospect ID</Typography>
                  <Typography>{pipelineProspect.prospectId}</Typography>
                </ProspectInfoItem>
              )}
              {useJobCode && (
                <ProspectInfoItem>
                  <Typography>Job code</Typography>
                  <Typography>{pipelineProspect.jobCode}</Typography>
                </ProspectInfoItem>
              )}
            </ProspectInfo>
            {pipelineProspect?.statusDate && (
              <Typography color={tokens.TINTS_OFF_WHITE_20}>
                {daysAgoText}
              </Typography>
            )}
            <ViewMoreSection>
              {isOpen && (
                <ActionContainer>
                  {isOwner && (
                    <>
                      <StyledButton
                        iconLeft={<SmallIcon name={SvgNames.Account} />}
                        color="tertiary"
                        onClick={navigateToCustomerProfile}>
                        View customer profile
                      </StyledButton>
                      <StyledButton
                        iconLeft={<SmallIcon name={SvgNames.Home} />}
                        color="tertiary"
                        onClick={
                          isMobile
                            ? mobileOpenOfferPresentation
                            : openOfferPresentation
                        }>
                        View offer presentation
                      </StyledButton>
                      {isNewPresentationEnabled && (
                        <StyledButton
                          iconLeft={<SmallIcon name={SvgNames.Home} />}
                          color="tertiary"
                          onClick={openNewOfferPresentation}>
                          View NEW offer presentation
                        </StyledButton>
                      )}
                      {canSendShareBill && (
                        <StyledButton
                          iconLeft={<SmallIcon name={SvgNames.Share} />}
                          color="tertiary"
                          onClick={() => setIsShareBillOpen(true)}>
                          Ask customer to share bill
                        </StyledButton>
                      )}
                      {canSendOfferPresentation && (
                        <StyledButton
                          iconLeft={<SmallIcon name={SvgNames.Share} />}
                          color="tertiary"
                          onClick={() => setIsSharePresentationOpen(true)}>
                          Send offer presentation
                        </StyledButton>
                      )}
                      <StyledButton
                        iconLeft={
                          <SmallIcon name={SvgNames.ArrowSquareOutSvg} />
                        }
                        color="tertiary"
                        onClick={viewInSalesforce}>
                        View opportunity
                      </StyledButton>
                    </>
                  )}
                  {projectUrl && (
                    <StyledButton
                      iconLeft={<SmallIcon name={SvgNames.ArrowSquareOutSvg} />}
                      color="tertiary"
                      onClick={viewProject}>
                      View project
                    </StyledButton>
                  )}
                  <StyledButton
                    iconLeft={<SmallIcon name={SvgNames.Copy} />}
                    color="tertiary"
                    onClick={copyCustomerInformation}>
                    Copy customer information
                  </StyledButton>
                </ActionContainer>
              )}
              <ViewMoreButton
                onClick={() => setIsOpen((current) => !current)}
                iconRight={
                  isOpen ? (
                    <SvgIcon name={SvgNames.ChevronUp} />
                  ) : (
                    <SvgIcon name={SvgNames.ChevronDown} />
                  )
                }
                color="tertiary">
                {isOpen ? 'Collapse' : 'View more'}
              </ViewMoreButton>
            </ViewMoreSection>
          </PipelineCardBodyContainer>
        </PipelineStyledCard>
        {createPortal(
          <StyledSlidingDrawer
            isOpen={isChatOpen}
            onClose={() => setIsChatOpen(false)}
            closeOnOutsideClick={true}
            closeOnEscape={true}
            styles={{
              width: isMobile ? '100%' : isTablet ? '75%' : '66%',
              maxWidth: isMobile ? '100vw' : '100%',
              height: isMobile ? '100%' : undefined,
              left: isMobile ? 0 : 'auto',
              right: 0,
            }}>
            {pipelineProspect.contactId && (
              <ChatIframe contactId={pipelineProspect.contactId} drawer />
            )}
          </StyledSlidingDrawer>,
          drawerHost ?? document.body,
        )}
      </>
    );
  },
);

const SmallIcon = styled(SvgIcon)`
  width: 16px !important;
  height: 16px !important;
`;

const ActionContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: start;
  padding: 6px 0;
`;

const ViewMoreSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${tokens.TINTS_OFF_WHITE_50};
  padding: 6px 16px;
  margin-left: -16px;
  margin-right: -16px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const ViewMoreButton = styled(Button)`
  padding: 0;
`;

const StyledButton = styled(Button)`
  padding: 6px 0;
`;

const PipelineStyledCard = styled(Card)`
  width: 303px;
  box-shadow: 0 4px 10px 0 #0000001f;
`;

const PipelineCardHeader = styled(CardHeader)`
  padding: 12px;
  height: auto;
  background: ${tokens.TINTS_CHARGE_BLUE_80};
  justify-content: space-between;
`;

const TitleContainer = styled.div`
  text-wrap: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const PipelineCardBodyContainer = styled.div`
  margin: 1rem !important;
  margin-bottom: 0 !important;
`;

const StyledPipelineUrgentActionBanner = styled(PipelineUrgentActionBanner)`
  margin: 0 !important;
  padding: 0 !important;
  border-bottom: 1px solid ${tokens.TINTS_OFF_WHITE_40};

  > dialog {
    border: none;
    position: static !important;
  }
`;

const Flex = styled.div`
  display: flex;
`;

const PipelineCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const PipelineStatus = styled.div`
  margin-top: 1rem;
  border-top: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &.single .status-complete,
  &.single .status-loading {
    display: none;
  }

  &.single .status-incomplete ~ .status-incomplete {
    display: none;
  }
`;

const MenuButton = styled(Button)`
  padding: 0.25rem;
`;

const SalesRepInfo = styled.div`
  margin-top: 1rem;
  border-top: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  padding-top: 1rem;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
`;

const ProspectInfo = styled.div`
  display: none;

  &.visible {
    display: block;
  }
`;

const UpcomingAppointment = styled.div`
  border-top: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  color: ${tokens.TINTS_OFF_WHITE_20};
  font-size: 12px;
  padding-top: 10px;
  margin-bottom: 10px;
`;

const HomeReviewLayout = styled.div`
  display: flex;
  justify-content: space-between;
`;

const SurveyPill = styled(Pill)`
  padding: 0.5rem !important;
  align-self: center;

  > p {
    line-height: normal;
  }
`;

const CommissionPillContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const CommissionPill = styled(Pill)`
  padding: 0.5rem 0.75rem !important;
  width: 100%;

  > p {
    line-height: 1.2;
    font-size: 12px;
  }
`;

const ProspectInfoItem = styled.div`
  border-top: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  padding: 1rem 0;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;

  > button {
    overflow: hidden;

    > span {
      text-wrap: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }

  > a {
    color: black;
  }
`;

const IconContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const ChatIconWithBadge = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
`;

const StyledSvgIcon = styled(SvgIcon)`
  min-width: 24px !important;
  min-height: 24px !important;
  width: 24px !important;
  height: 24px !important;
`;

const UnreadCount = styled.span`
  position: absolute;
  font-size: 10px;
  font-weight: bold;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const StyledSlidingDrawer = styled(SlidingDrawer)`
  margin: 0px !important;
  scrollbar-width: none;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100% !important;
    max-width: 100vw !important;
    height: 100% !important;
    left: 0 !important;
    right: 0 !important;
  }
`;

export { PipelineCard };
